import {
	Check,
	Copy,
	ExternalLink,
	Globe,
	Loader2,
	Lock,
	Search,
	Users,
	UserPlus,
	X,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { toSharedDashboard, useReportBuilderStore } from "../../../hooks/reportBuilder.hooks";
import {
	DashboardRecipient,
	DashboardVisibility,
	ReportDashboard,
} from "../../../models/ReportBuilder";
import { cn } from "@/lib/utils";
import {
	useCreateShare,
	useDeleteShare,
	useUpdateShare,
} from "../../../networking/report-builder.hooks";
import { useInternalUsers } from "../../../networking/internal-user.hooks";
import { useAuthStore } from "../../../stores/auth.store";

interface ShareDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	dashboard: ReportDashboard;
}

const shareUrl = (path: string) => `${window.location.origin}${path}`;

/**
 * Åtkomst and the public link are two independent things, not one ladder: a
 * report can be private inside the app and still hang on a TV, or be shared
 * with three named colleagues and nowhere else. Publishing therefore no longer
 * touches `visibility`.
 */
const ACCESS: {
	value: DashboardVisibility;
	icon: React.ElementType;
	title: string;
	description: string;
}[] = [
	{ value: "private", icon: Lock, title: "Bara jag", description: "Ingen annan ser dashboarden." },
	{
		value: "users",
		icon: UserPlus,
		title: "Valda personer",
		description: "Bara de du väljer kan öppna och läsa den.",
	},
	{
		value: "organisation",
		icon: Users,
		title: "Alla i organisationen",
		description: "Alla interna användare kan öppna och läsa den, men inte ändra den.",
	},
];

const fullName = (user: { firstName?: string; lastName?: string; email?: string }) =>
	[user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email || "";

/** Vem som når dashboarden, och om den dessutom ligger på en publik länk. */
const ShareDialog: React.FC<ShareDialogProps> = ({ open, onOpenChange, dashboard }) => {
	const store = useReportBuilderStore();
	const create = useCreateShare();
	const update = useUpdateShare();
	const remove = useDeleteShare();
	const myUserId = useAuthStore((s) => s.userId);
	const [pin, setPin] = useState("");
	const [copied, setCopied] = useState(false);
	const [search, setSearch] = useState("");
	const share = dashboard.share;
	const visibility = dashboard.visibility ?? "private";
	const recipients = dashboard.sharedWith ?? [];
	const busy = create.isLoading || update.isLoading || remove.isLoading;
	const error = create.error ?? update.error ?? remove.error;

	// Only fetched once the dialog is open on "Valda personer": the list is the
	// whole organisation, and it is the picker's data, nobody else's.
	const { users, isLoading: loadingUsers } = useInternalUsers(
		{ view: "select_option" },
		{ enabled: open && visibility === "users" }
	);

	useEffect(() => {
		if (open) {
			setPin("");
			setCopied(false);
			setSearch("");
		}
	}, [open]);

	const chosen = useMemo(() => new Set(recipients.map((r) => r.id)), [recipients]);

	const candidates = useMemo(() => {
		const term = search.trim().toLowerCase();
		return users
			.filter((u: any) => u.id !== myUserId && !u.isDeleted)
			.filter((u: any) => !term || fullName(u).toLowerCase().includes(term))
			.sort((a: any, b: any) => fullName(a).localeCompare(fullName(b), "sv"));
	}, [users, myUserId, search]);

	const setVisibility = (next: DashboardVisibility) => store.setVisibility(dashboard.id, next);

	const toggleRecipient = (user: { id: string; firstName?: string; lastName?: string }) => {
		const next: DashboardRecipient[] = chosen.has(user.id)
			? recipients.filter((r) => r.id !== user.id)
			: [...recipients, { id: user.id, name: fullName(user) || undefined }];
		store.setSharedWith(dashboard.id, next);
	};

	const removeRecipient = (id: string) =>
		store.setSharedWith(
			dashboard.id,
			recipients.filter((r) => r.id !== id)
		);

	const publish = () =>
		create.mutate(
			{ dashboard: toSharedDashboard(dashboard), pin: pin.trim() || undefined },
			{ onSuccess: (info) => store.setShare(dashboard.id, info) }
		);

	const unpublish = () =>
		share &&
		remove.mutate(
			{ shareId: share.shareId },
			{ onSuccess: () => store.setShare(dashboard.id, undefined) }
		);

	const changePin = (next: string) =>
		share &&
		update.mutate(
			{ shareId: share.shareId, pin: next },
			{ onSuccess: (info) => store.setShare(dashboard.id, info) }
		);

	const copy = async () => {
		if (!share) return;
		await navigator.clipboard.writeText(shareUrl(share.path));
		setCopied(true);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="z-[1300] flex max-h-[90vh] max-w-lg flex-col overflow-hidden">
				<DialogHeader>
					<DialogTitle>Delning</DialogTitle>
					<DialogDescription>
						Välj vilka som når dashboarden. Ändringar du gör syns för dem direkt — bara du kan ändra
						den.
					</DialogDescription>
				</DialogHeader>

				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
					<section className="flex flex-col gap-2">
						<h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Åtkomst</h3>
						{ACCESS.map((option) => {
							const Icon = option.icon;
							const active = option.value === visibility;
							return (
								<button
									key={option.value}
									type="button"
									disabled={busy}
									onClick={() => setVisibility(option.value)}
									className={cn(
										"flex w-full items-start gap-3 rounded-lg border border-solid px-4 py-3 text-left transition-colors disabled:opacity-60",
										active
											? "border-brand bg-brand/5"
											: "border-gray-300 bg-white hover:border-brand hover:bg-brand/5"
									)}
								>
									<Icon
										className={cn(
											"mt-0.5 h-5 w-5 shrink-0",
											active ? "text-brand" : "text-gray-500"
										)}
									/>
									<span className="min-w-0">
										<span className="block text-sm font-semibold text-gray-900">
											{option.title}
										</span>
										<span className="block text-xs text-gray-500">{option.description}</span>
									</span>
									{active && <Check className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-brand" />}
								</button>
							);
						})}

						{visibility === "users" && (
							<div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-3">
								{recipients.length > 0 && (
									<ul className="flex flex-wrap gap-1.5">
										{recipients.map((recipient) => (
											<li
												key={recipient.id}
												className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs text-gray-800 ring-1 ring-gray-300"
											>
												{recipient.name || recipient.id}
												<button
													type="button"
													onClick={() => removeRecipient(recipient.id)}
													className="text-gray-400 hover:text-gray-700"
													aria-label={`Ta bort ${recipient.name || recipient.id}`}
												>
													<X className="h-3 w-3" />
												</button>
											</li>
										))}
									</ul>
								)}

								<div className="relative">
									<Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
									<input
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder="Sök kollega"
										className="w-full rounded-md border border-solid border-gray-300 bg-white py-1.5 pl-7 pr-2 text-sm"
									/>
								</div>

								<ul className="max-h-48 overflow-y-auto rounded-md bg-white ring-1 ring-gray-200">
									{loadingUsers && <li className="px-3 py-2 text-xs text-gray-500">Hämtar…</li>}
									{!loadingUsers && candidates.length === 0 && (
										<li className="px-3 py-2 text-xs text-gray-500">Inga träffar.</li>
									)}
									{candidates.map((user: any) => (
										<li key={user.id}>
											<button
												type="button"
												onClick={() => toggleRecipient(user)}
												className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-brand/5"
											>
												<span
													className={cn(
														"flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid",
														chosen.has(user.id)
															? "border-brand bg-brand text-white"
															: "border-gray-300 bg-white"
													)}
												>
													{chosen.has(user.id) && <Check className="h-3 w-3" />}
												</span>
												<span className="truncate text-gray-800">{fullName(user)}</span>
												{user.active === false && (
													<span className="ml-auto text-xs text-gray-400">inaktiv</span>
												)}
											</button>
										</li>
									))}
								</ul>

								{recipients.length === 0 && (
									<p className="text-xs text-gray-500">
										Ingen vald ännu — dashboarden når bara dig tills du väljer någon.
									</p>
								)}
							</div>
						)}
					</section>

					<section className="flex flex-col gap-2 border-t border-solid border-gray-200 pt-3">
						<div className="flex items-center justify-between gap-3">
							<span className="flex items-start gap-3">
								<Globe
									className={cn("mt-0.5 h-5 w-5 shrink-0", share ? "text-brand" : "text-gray-500")}
								/>
								<span className="min-w-0">
									<span className="block text-sm font-semibold text-gray-900">Publik länk</span>
									<span className="block text-xs text-gray-500">
										Vem som helst med länken ser den utan inloggning, t.ex. på en TV.
									</span>
								</span>
							</span>
							<button
								type="button"
								disabled={busy}
								onClick={() => (share ? unpublish() : publish())}
								className={cn(
									"shrink-0 rounded-md border border-solid px-3 py-1.5 text-sm disabled:opacity-60",
									share
										? "border-gray-300 bg-white text-gray-700"
										: "border-brand bg-brand text-white"
								)}
							>
								{share ? "Stäng av" : "Slå på"}
							</button>
						</div>

						{share && (
							<div className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3">
								<div className="flex items-center gap-2">
									<input
										readOnly
										value={shareUrl(share.path)}
										className="flex-1 rounded-md border border-solid border-gray-300 bg-white px-2 py-1.5 text-sm"
										onFocus={(e) => e.currentTarget.select()}
									/>
									<button
										type="button"
										onClick={copy}
										className="rounded-md border border-solid bg-white px-2 py-1.5 text-sm"
										title="Kopiera länk"
									>
										<Copy className="h-4 w-4" />
									</button>
									<a
										href={shareUrl(share.path)}
										target="_blank"
										rel="noreferrer"
										className="rounded-md border border-solid bg-white px-2 py-1.5 text-sm"
										title="Öppna"
									>
										<ExternalLink className="h-4 w-4" />
									</a>
								</div>
								{copied && <span className="text-xs text-brand">Länk kopierad.</span>}
								<div className="flex flex-wrap items-center gap-2 text-sm">
									<span className="text-gray-600">
										{share.hasPin ? "Skyddad med PIN." : "Ingen PIN — alla med länken kommer in."}
									</span>
									<input
										value={pin}
										onChange={(e) => setPin(e.target.value)}
										inputMode="numeric"
										placeholder={share.hasPin ? "Ny PIN" : "Sätt PIN"}
										className="w-28 rounded-md border border-solid border-gray-300 px-2 py-1 text-sm"
									/>
									<button
										type="button"
										disabled={busy || !pin.trim()}
										onClick={() => changePin(pin)}
										className="rounded-md border border-solid bg-white px-2 py-1 text-sm disabled:opacity-50"
									>
										Spara PIN
									</button>
									{share.hasPin && (
										<button
											type="button"
											disabled={busy}
											onClick={() => changePin("")}
											className="text-xs text-gray-500 hover:underline"
										>
											Ta bort PIN
										</button>
									)}
								</div>
								<p className="text-xs text-gray-400">
									Senast publicerad {new Date(share.updatedAt).toLocaleString("sv-SE")}. Länken
									innehåller ingen SQL och ger bara läsning av denna dashboard.
								</p>
							</div>
						)}

						{!share && (
							<label className="text-sm">
								<span className="mb-1 block text-gray-600">PIN-kod för publik länk (valfritt)</span>
								<input
									value={pin}
									onChange={(e) => setPin(e.target.value)}
									inputMode="numeric"
									placeholder="t.ex. 4821"
									className="w-40 rounded-md border border-solid border-gray-300 px-2 py-1.5 text-sm"
								/>
							</label>
						)}
					</section>
				</div>

				{error && (
					<p className="text-xs text-red-600">
						{error.error?.detail || error.error?.title || "Något gick fel"}
					</p>
				)}

				<DialogFooter>
					{busy && <Loader2 className="mr-auto h-4 w-4 animate-spin text-gray-400" />}
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-md border border-solid px-3 py-1.5 text-sm"
					>
						Stäng
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ShareDialog;

/**
 * Keeps a published share in step with the local report: any change to
 * name, range, comparison or widgets is pushed after a short debounce.
 */
export function useShareSync(dashboard: ReportDashboard | undefined) {
	const store = useReportBuilderStore();
	const update = useUpdateShare();
	const lastSent = useRef<string>();
	const timer = useRef<number>();

	const shareId = dashboard?.share?.shareId;
	const serialized = dashboard ? JSON.stringify(toSharedDashboard(dashboard)) : undefined;

	useEffect(() => {
		if (!shareId || !serialized || !dashboard) return;
		if (lastSent.current === undefined) {
			lastSent.current = serialized; // what is published now; do not re-send on mount
			return;
		}
		if (lastSent.current === serialized) return;
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(() => {
			lastSent.current = serialized;
			update.mutate(
				{ shareId, dashboard: JSON.parse(serialized) },
				{ onSuccess: (info) => store.setShare(dashboard.id, info) }
			);
		}, 1500);
		return () => window.clearTimeout(timer.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shareId, serialized]);

	useEffect(() => {
		lastSent.current = undefined;
	}, [shareId]);
}
